import {
  Chip,
  IconButton,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  CustomTabPanel,
  DeleteModal,
  PageListLayout,
  usePermission,
} from "../../../shared";
import { useIngredients } from "../hooks/UseIngredient";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUnits } from "../../MasterData/hooks";
import { IngredientsBasicDetails } from "../components/IngredientsBasicDetails";
import { IngredientsLabels } from "../components/IngredientsLabels";
import { useLabelPrinting } from "../../LabelPrinting/hooks/useLabelPrinting";
import { IngredientReferences } from "../components/IngredientReferences";
import { CostChangeHistory, StockTake } from "../components";
import { useLayoutEffect } from "react";

export const IngredientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { isVisible } = useLabelPrinting({ isList: true });
  const {
    ingredient,
    confirmDelete,
    onDelete,
    deleteInfo,
    resetDeleteInfo,
    state,
    isEditable,
    isDeletable,
    globalData,
  } = useIngredients({
    detId: id,
    isIngredientlist: false,
    isTaxgrouplist: false,
    isTaglist: false,
    isList: false,
  });
  const { listQueryData } = useUnits({ list: true });
  const optionsUnits = listQueryData?.data
    ?.filter((i) => i?.ignore === 0)
    ?.map((item) => ({
      name: item.symbol,
      id: item.id,
    }));

  const isStockTakeVisible = usePermission("stock-take-view");

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/ingredients")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Ingredients
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {ingredient?.data?.name}
    </Typography>,
  ];

  const handleChange = (event, newValue) => {
    navigate(`/ingredients/details/${id}${newValue}`);
  };

  // Re-navigate to parent details, if parent ingredient id is exist.
  useLayoutEffect(() => {
    if (ingredient.data?.parent_ingredient_id)
      navigate(`/ingredients/details/${ingredient.data.parent_ingredient_id}`);
  }, [ingredient?.data?.parent_ingredient_id]);

  return (
    <PageListLayout
      breadcrumbs={breadcrumbs}
      headerText={
        <Stack
          direction="row"
          justifyContent="space-between"
          height="62px"
          bgcolor="white"
          sx={{
            position: "sticky",
            zIndex: "99px",
            top: "0px",
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            flexDirection="row"
            gap="12px"
          >
            <Typography variant="h6" fontWeight={600} fontSize="24px">
              {ingredient?.data?.name}
            </Typography>
            {ingredient?.data?.status ? (
              <Chip
                size="small"
                color="primary"
                variant="filled"
                label="ACTIVE"
              />
            ) : (
              <Chip size="small" color="gray" label="INACTIVE" />
            )}
          </Stack>
        </Stack>
      }
      rightContent={
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          {isEditable && (
            <IconButton onClick={(e) => navigate(`/ingredients/edit/${id}`)}>
              <img alt="edit" src="/icons/ic_edit.png" />
            </IconButton>
          )}
          {isDeletable && (
            <IconButton onClick={() => onDelete(id, "/ingredients")}>
              <img alt="delete" src="/icons/ic_trash_sm.png" />
            </IconButton>
          )}
        </Stack>
      }
      tabs={
        <Tabs value={search} onChange={handleChange}>
          <Tab label="Basic Details" value={""} />
          {isVisible && <Tab label="Labels" value={"?labels"} />}
          <Tab label="References" value={"?references"} />
          <Tab label="Cost Change History" value="?cost-change-history" />
          {isStockTakeVisible && <Tab label="Stocktake" value="?stock-take" />}
        </Tabs>
      }
      sx={{
        px: "16px",
        pt: 0,
        backgroundColor: "#FFFFFF",
        minHeight: "calc(100vh - 145px)",
      }}
    >
      <CustomTabPanel value={search} index={""} border={false}>
        <IngredientsBasicDetails
          state={state}
          ingredient={ingredient}
          optionsUnits={optionsUnits}
          isEditable={isEditable}
          isDeletable={isDeletable}
          id={id}
          onDelete={onDelete}
          globalData={globalData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?labels"} border={false}>
        <IngredientsLabels
          state={state}
          ingredient={ingredient}
          optionsUnits={optionsUnits}
          isEditable={isEditable}
          isDeletable={isDeletable}
          ingredientId={id}
          onDelete={onDelete}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?references"} border={false}>
        <IngredientReferences id={id} />
      </CustomTabPanel>
      <CustomTabPanel
        value={search}
        index="?cost-change-history"
        style={{ paddingTop: 16 }}
      >
        <CostChangeHistory />
      </CustomTabPanel>
      {isStockTakeVisible && (
        <CustomTabPanel value={search} index={"?stock-take"} border={false}>
          <StockTake optionsUnits={optionsUnits} ingredient={ingredient} />
        </CustomTabPanel>
      )}
      <DeleteModal
        open={deleteInfo.show}
        handleClose={() => resetDeleteInfo()}
        onConfirm={() => confirmDelete()}
      />
    </PageListLayout>
  );
};
